import * as React from "react";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { RewardsBannerFTNodeOptions, RewardsBannerProps } from "./typings";
import { ViewMedium, ViewSmall, Viewport } from "@shared-ui/viewport-context";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkLink } from "uitk-react-link";
import { UitkText, UitkParagraph } from "uitk-react-text";
import { UitkIcon } from "uitk-react-icons";
import { UitkList, UitkListItem } from "uitk-react-list";
import { UitkPrimaryButton, UitkTertiaryButton } from "uitk-react-button";
import { UitkMark, UitkMarkSize } from "uitk-react-mark";
import { UitkImage, UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { useLocalization } from "@shared-ui/localization-context";

export const RewardsBanner = withStores(
  "flexModuleModelStore",
  "context"
)(
  observer((props: RewardsBannerProps) => {
    const { templateComponent, flexModuleModelStore, context } = props;
    const { formatText } = useLocalization();

    if (!templateComponent || !flexModuleModelStore) {
      return null;
    }

    const { metadata } = templateComponent;
    const { id } = metadata;
    const model = flexModuleModelStore.getModel(id) as RewardsBannerFTNodeOptions | null;

    if (!model) {
      return null;
    }

    const { illustrationImageSrc, logoImageSrc, findOutMoreLink, joinNowLink, signInLink, hideCTAs } = model;

    // 300000011 is HCOM_SE and 300000014 is HCOM_CH
    const memberDiscount = context.site.id === 300000011 || context.site.id === 300000014 ? 10 : 15;
    const memberDiscountText =
      context.site.id === 300000014
        ? formatText("rewardsBanner.unlockMemberPricesNoAverage", "<b>", "</b>", memberDiscount)
        : formatText("rewardsBanner.unlockMemberPrices", "<b>", "</b>", memberDiscount);

    const bannerCtas = !hideCTAs && (
      <UitkLayoutFlexItem>
        <UitkSpacing margin={{ blockstart: "two" }}>
          <UitkLayoutFlex space="two">
            <UitkLayoutFlexItem>
              <UitkPrimaryButton
                type="button"
                tag="a"
                aria-label={formatText("rewardsBanner.joinNow")}
                href={joinNowLink}
              >
                {formatText("rewardsBanner.joinNow")}
              </UitkPrimaryButton>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <UitkTertiaryButton
                type="button"
                tag="a"
                aria-label={formatText("rewardsBanner.signIn")}
                href={signInLink}
              >
                {formatText("rewardsBanner.signIn")}
              </UitkTertiaryButton>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkSpacing>
      </UitkLayoutFlexItem>
    );

    const rewardsBannerBase = (
      <>
        <UitkLayoutFlexItem>
          <UitkLayoutFlex direction="column">
            <UitkLayoutFlexItem>
              <UitkFigure className="rewards-banner-illustration" ratio={UitkFigureAspectRatioType.R3_2}>
                <UitkImage alt="" src={illustrationImageSrc} />
              </UitkFigure>
            </UitkLayoutFlexItem>
            <Viewport>
              <ViewMedium>
                <UitkLayoutFlexItem>
                  <UitkParagraph>
                    <UitkLink>
                      <a href={findOutMoreLink}> {formatText("rewardsBanner.seeTermsAndConditions")}</a>
                    </UitkLink>
                  </UitkParagraph>
                </UitkLayoutFlexItem>
              </ViewMedium>
            </Viewport>
          </UitkLayoutFlex>
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem>
          <UitkLayoutFlex direction="column" justifyContent={hideCTAs ? "center" : "start"}>
            <UitkLayoutFlexItem>
              <div>
                <UitkMark url={logoImageSrc} size={UitkMarkSize.MEDIUM} />
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <UitkSpacing margin={{ blockstart: "six" }}>
                <UitkList bullet="icon-alternate" size={1}>
                  <UitkListItem>
                    <UitkIcon name="check" />
                    <UitkText inline>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: memberDiscountText,
                        }}
                      />
                    </UitkText>
                  </UitkListItem>
                  <UitkListItem>
                    <UitkIcon name="check" />
                    <UitkText inline>
                      <span
                        dangerouslySetInnerHTML={{ __html: formatText("rewardsBanner.getRewardNight", "<b>", "</b>") }}
                      />
                    </UitkText>
                  </UitkListItem>
                  <UitkListItem>
                    <UitkIcon name="check" />
                    <UitkText inline>
                      <span
                        dangerouslySetInnerHTML={{ __html: formatText("rewardsBanner.rackUpRewards", "<b>", "</b>") }}
                      />
                    </UitkText>
                  </UitkListItem>
                </UitkList>
              </UitkSpacing>
            </UitkLayoutFlexItem>
            {bannerCtas}
            <Viewport>
              <ViewSmall>
                <UitkLayoutFlexItem>
                  <UitkParagraph>
                    <UitkLink>
                      <a href={findOutMoreLink}> {formatText("rewardsBanner.seeTermsAndConditions")}</a>
                    </UitkLink>
                  </UitkParagraph>
                </UitkLayoutFlexItem>
              </ViewSmall>
              <ViewMedium>
                <></>
              </ViewMedium>
            </Viewport>
          </UitkLayoutFlex>
        </UitkLayoutFlexItem>
      </>
    );

    return (
      <UitkSpacing padding="four">
        <section aria-label="Rewards banner">
          <Viewport>
            <ViewSmall>
              <UitkLayoutFlex data-testid="rewards-banner-small" direction="column" space="two">
                {rewardsBannerBase}
              </UitkLayoutFlex>
            </ViewSmall>
            <ViewMedium>
              <UitkLayoutFlex data-testid="rewards-banner-medium-large" justifyContent="center" space="six">
                {rewardsBannerBase}
              </UitkLayoutFlex>
            </ViewMedium>
          </Viewport>
        </section>
      </UitkSpacing>
    );
  })
);

export default RewardsBanner;
